import { refreshCsrfToken } from './AuthorizedApiBase';
import { log } from '@/lib/utils/logger';

type HttpClientType = {
  fetch(url: RequestInfo, init?: RequestInit): Promise<Response>;
};

/**
 * HttpClient which uses either node or browser fetch.
 * Needs to be provided to the generated ApiClients to avoid them using the global window.
 * Also updates last interaction timestamp on successful API calls.
 * Automatically refreshes CSRF token on 403 responses and retries the request once.
 */
export class HttpClient implements HttpClientType {
  private static inactivityManager: { updateLastInteraction: () => void } | null = null;
  private static attemptSessionRecovery: (() => Promise<boolean>) | null = null;
  private baseUrl: string | null = null;
  private csrfRefreshInProgress: Promise<void> | null = null;

  /**
   * Set the inactivity manager instance
   * This should be called when the app initializes
   */
  static setInactivityManager(manager: { updateLastInteraction: () => void } | null) {
    HttpClient.inactivityManager = manager;
  }

  /** Registers JWT refresh + eppn verification for 401 responses (browser only). */
  static setSessionRecovery(handler: (() => Promise<boolean>) | null) {
    HttpClient.attemptSessionRecovery = handler;
  }

  private isAuthEndpoint(url: RequestInfo): boolean {
    if (typeof url !== 'string') {
      return false;
    }
    return /\/auth\/(jwt|signout|csrf)(\/|$|\?)/i.test(url);
  }

  /**
   * Set the base URL for CSRF token refresh
   * Should be called when creating repositories
   */
  setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Extracts base URL from a request URL
   * Assumes URLs are in format: baseUrl/api/v...
   */
  private extractBaseUrl(url: RequestInfo): string | null {
    if (this.baseUrl) {
      return this.baseUrl;
    }

    if (typeof url === 'string') {
      // Try to extract baseUrl by finding /api/ in the URL
      const apiIndex = url.indexOf('/api/');
      if (apiIndex > 0) {
        return url.substring(0, apiIndex);
      }
    }

    return null;
  }

  async fetch(url: RequestInfo, init?: RequestInit): Promise<Response> {
    // Check if this is already a retry attempt (prevent infinite loops)
    const isCsrfRetry = (init as Record<string, unknown>)?.['__csrf_retry'] === true;
    const isAuthRetry = (init as Record<string, unknown>)?.['__auth_retry'] === true;

    let response = await fetch(url, init);

    // If we get a 403 and haven't retried yet, check if it's a CSRF error
    if (response.status === 403 && !isCsrfRetry) {
      const baseUrl = this.extractBaseUrl(url);
      if (baseUrl) {
        // Check response body for CSRF validation error
        // Use clone() to avoid consuming the original response
        let isCsrfError = false;
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('text/') || contentType.includes('application/json')) {
            const responseText = await response.clone().text();
            isCsrfError =
              responseText.includes('CSRF validation failed') ||
              responseText.includes('CSRF') ||
              responseText.toLowerCase().includes('csrf');
          } else {
            // For non-text responses, assume 403 might be CSRF-related and try refresh
            // This is safer than ignoring potential CSRF issues
            isCsrfError = true;
          }
        } catch (error) {
          // If we can't read the response, assume it might be CSRF-related
          log.debug('[HttpClient] Could not read response body, assuming CSRF error:', error);
          isCsrfError = true;
        }

        if (isCsrfError) {
          log.debug('[HttpClient] Received 403 with CSRF error, attempting CSRF token refresh');

          // Ensure only one refresh happens at a time
          if (!this.csrfRefreshInProgress) {
            this.csrfRefreshInProgress = refreshCsrfToken(baseUrl);
          }

          try {
            await this.csrfRefreshInProgress;
            this.csrfRefreshInProgress = null;

            // Mark this as a retry attempt to prevent infinite loops
            // Remove the flag from the init object before passing it to native fetch
            const retryInit: RequestInit = {
              ...init,
            };
            (retryInit as Record<string, unknown>)['__csrf_retry'] = true;

            // Recursively call this.fetch (not native fetch) so the retry goes through our wrapper
            // The __csrf_retry flag will prevent infinite loops
            response = await this.fetch(url, retryInit);
            log.debug('[HttpClient] Request retried after CSRF refresh');
          } catch (error) {
            this.csrfRefreshInProgress = null;
            log.error('[HttpClient] CSRF refresh failed, returning original 403 response:', error);
            // Return the original 403 response if refresh fails
          }
        }
      }
    }

    if (
      response.status === 401 &&
      !isAuthRetry &&
      typeof window !== 'undefined' &&
      !this.isAuthEndpoint(url) &&
      HttpClient.attemptSessionRecovery
    ) {
      log.debug('[HttpClient] Received 401, attempting session recovery');
      try {
        const recovered = await HttpClient.attemptSessionRecovery();
        if (recovered) {
          const retryInit: RequestInit = { ...init };
          (retryInit as Record<string, unknown>)['__auth_retry'] = true;
          response = await this.fetch(url, retryInit);
          log.debug('[HttpClient] Request retried after session recovery');
        }
      } catch (error) {
        log.error('[HttpClient] Session recovery failed:', error);
      }
    }

    // Only update timestamp for successful API calls
    if (response.ok && typeof window !== 'undefined' && HttpClient.inactivityManager) {
      HttpClient.inactivityManager.updateLastInteraction();
    }

    return response;
  }
}
